import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  SENSOR_TESTS, MOVEMENT_LABELS, createSensorDiagnostic,
  hasSensorValues, captureMatchesRecord,
} from "../../lib/sensorDiagnostics";

const formatValue = (value) => typeof value === "number" && Number.isFinite(value) ? value.toFixed(3) : "Unavailable";

function Readings({ sensor, sample, label }) {
  const groups = sensor === "acceleration"
    ? [["Acceleration", sample?.acceleration], ["Including gravity", sample?.acceleration_including_gravity]]
    : [[SENSOR_TESTS[sensor].label, sample?.[sensor]]];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left tabular-nums">
        <caption className="text-left text-slate-300 py-2">{label} · {SENSOR_TESTS[sensor].unit}</caption>
        <thead><tr><th scope="col">Reading</th>{(sensor === "acceleration" ? ["x", "y", "z"] : ["alpha", "beta", "gamma"]).map((axis) => <th scope="col" key={axis}>{axis}</th>)}</tr></thead>
        <tbody>{groups.map(([title, values]) => <tr key={title}><th scope="row" className="py-2 font-normal">{title}</th>{(sensor === "acceleration" ? ["x", "y", "z"] : ["alpha", "beta", "gamma"]).map((axis) => <td key={axis}>{formatValue(values?.[axis])}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

export default function SensorDiagnostics({ lessonId }) {
  const [sensor, setSensor] = useState("acceleration");
  const [movement, setMovement] = useState("stationary");
  const [mount, setMount] = useState("handheld");
  const [phase, setPhase] = useState("idle");
  const [sample, setSample] = useState(null);
  const [count, setCount] = useState(0);
  const [pending, setPending] = useState(null);
  const [record, setRecord] = useState(null);
  const [error, setError] = useState("");
  const controller = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      controller.current?.stop("left_briefing");
    };
  }, []);

  const persist = async (capture) => {
    if (mounted.current) { setPhase("saving"); setPending(capture); setError(""); }
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/work-mode/lessons/${encodeURIComponent(lessonId)}/sensor-diagnostics`;
    try {
      await axios.post(base, capture, { withCredentials: true });
      const { data } = await axios.get(`${base}/${capture.capture_id}`, { withCredentials: true });
      if (!captureMatchesRecord(capture, data)) throw new Error("Saved readings did not match. Keep this check and retry.");
      if (mounted.current) { setRecord(data); setPending(null); setPhase("saved"); }
    } catch (failure) {
      if (mounted.current) {
        setPhase("unsaved");
        setError(failure.response?.status === 401 || failure.response?.status === 403
          ? "Sign in with this lesson's instructor account to save the check."
          : "Readback is not confirmed. Keep this screen open and retry saving when connected.");
      }
    }
  };

  const start = async () => {
    setError(""); setRecord(null); setSample(null); setCount(0); setPhase("permission");
    const next = createSensorDiagnostic({
      sensor, movement, mount,
      onSample: (value, total) => { if (mounted.current) { setSample(value); setCount(total); } },
      onFinish: persist,
    });
    controller.current = next;
    try {
      const active = await next.start();
      if (mounted.current && active) setPhase("capturing");
    } catch (failure) {
      if (mounted.current) { setPhase("idle"); setError(failure.message || "Sensor access failed."); }
    }
  };

  const busy = ["permission", "capturing", "saving", "unsaved"].includes(phase);
  const storedLast = record?.capture.samples.at(-1);
  const signal = hasSensorValues(sensor, sample);
  const inputClass = "block mt-1 w-full min-h-11 bg-slate-800 text-white border border-slate-600 rounded px-3";
  return (
    <details className="border-y border-slate-700 py-3" data-testid="sensor-diagnostics">
      <summary className="min-h-11 cursor-pointer font-semibold">Check phone sensors</summary>
      <div className="space-y-3 py-2">
        <p className="text-sm text-slate-300">While parked, check one sensor at a time. Readings remain unverified and cannot be used by Karen to judge the driving. Keep this screen open until saving is confirmed.</p>
        <label className="block text-sm">Sensor
          <select className={inputClass} value={sensor} disabled={busy} onChange={(event) => { setSensor(event.target.value); setMovement("stationary"); setSample(null); setRecord(null); setCount(0); setPhase("idle"); }}>
            {Object.entries(SENSOR_TESTS).map(([value, item]) => <option key={value} value={value}>{item.label}</option>)}
          </select>
        </label>
        <label className="block text-sm">Movement to try
          <select className={inputClass} value={movement} disabled={busy} onChange={(event) => setMovement(event.target.value)}>
            {SENSOR_TESTS[sensor].movements.map((value) => <option key={value} value={value}>{MOVEMENT_LABELS[value]}</option>)}
          </select>
        </label>
        <label className="block text-sm">Phone position
          <select className={inputClass} value={mount} disabled={busy} onChange={(event) => setMount(event.target.value)}>
            <option value="handheld">Held in hand</option><option value="flat_surface">Flat surface</option><option value="vehicle_mount">In the car mount</option>
          </select>
        </label>
        <p className="text-sm text-slate-400">Use the phone's own axes, regardless of screen rotation. A movement choice records what you intend to try; it does not mark the sensor as passed.</p>
        {phase === "capturing" ? (
          <button type="button" className="min-h-11 border border-slate-500 rounded px-4" onClick={() => controller.current?.stop()}>Stop and save check</button>
        ) : phase === "unsaved" ? (
          <button type="button" className="min-h-11 border border-amber-500 rounded px-4" onClick={() => persist(pending)}>Retry saving check</button>
        ) : (
          <button type="button" className="min-h-11 border border-sky-500 rounded px-4 disabled:opacity-50" disabled={busy || !lessonId} onClick={start}>I'm parked — start 20-second check</button>
        )}
        <p role="status" className="text-sm text-slate-300">
          {phase === "permission" ? "Waiting for sensor permission…" : phase === "saving" ? "Saving and reading back…" : phase === "unsaved" ? "Check stopped. Save not confirmed." : phase === "saved" ? `Saved and read back: ${record.capture.samples.length} samples. Still unverified.` : phase === "capturing" ? "Reading sensor. Stops automatically after 20 seconds." : "Ready for a stationary check."}
        </p>
        {error && <p role="alert" className="text-sm text-amber-200">{error}</p>}
        {count > 0 && <>
          <Readings sensor={sensor} sample={sample} label="Latest device reading" />
          <p className="text-xs text-slate-400">{count} samples · {(sample.elapsed_ms / 1000).toFixed(2)} seconds · screen angle {formatValue(sample.screen_angle)}°</p>
          <p className="text-xs text-slate-400">Device time: {new Date(sample.device_epoch_ms).toISOString()}</p>
        </>}
        {(["capturing", "saved"].includes(phase) && !signal) && <p className="text-sm text-amber-200">{count ? "Events arrived, but the selected sensor has no values." : "No sensor readings received."} Permission alone does not prove a working sensor.</p>}
        {storedLast && <Readings sensor={record.capture.sensor} sample={storedLast} label="Latest saved reading" />}
        {record && <p className="text-xs text-slate-400">Saved movement: {MOVEMENT_LABELS[record.capture.movement]}. Stopped: {record.capture.stop_reason.replaceAll("_", " ")}. Calibration, vehicle axes and clock synchronization still need physical checks. Leaving this screen stops capture.</p>}
      </div>
    </details>
  );
}
